import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { EntryTimingProfile } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import { EntryTimingProfilePanel, formatEntryTimingBucketLabel, getEntryTimingCellEvidence } from '@/components/EntryTimingProfile';
import { showToast } from '@/components/Layout';
import { Button, Callout, FormSelect } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';
import {
    buildEntryTimingHypothesisInput,
    buildIdeaLabRulesEditorPath,
    canCreateEntryTimingHypothesis,
    formatHorizonLabel,
    LENS_LABELS,
    type IdeaLabLens,
    type IdeaLabWindowRole
} from '../ideaLabUtils';

interface IdeaLabEntryTimingProfileProps {
    entityType?: 'Asset' | 'AssetIndex' | 'Cryptocurrency' | undefined;
    runId: number;
    variantId: number;
    variantLabel: string;
    lens: IdeaLabLens;
    windowRole: IdeaLabWindowRole;
    horizons: readonly number[];
    /** Horizon shown first, e.g. the one selected in the report; defaults to the shortest. */
    initialHorizonSeconds?: number | null;
}

/** On-demand timing evidence for the exact variant/lens/window selected in the report matrix. */
const IdeaLabEntryTimingProfile = ({
    entityType,
    runId,
    variantId,
    variantLabel,
    lens,
    windowRole,
    horizons,
    initialHorizonSeconds = null
}: IdeaLabEntryTimingProfileProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [selectedHorizon, setSelectedHorizon] = useState<number | null>(
        initialHorizonSeconds !== null && horizons.includes(initialHorizonSeconds) ? initialHorizonSeconds : (horizons[0] ?? null)
    );
    const [selectedCell, setSelectedCell] = useState<EntryTimingProfile['cells'][number] | null>(null);
    const horizon = selectedHorizon !== null && horizons.includes(selectedHorizon) ? selectedHorizon : (horizons[0] ?? null);
    const hypothesisCreationEnabled = canCreateEntryTimingHypothesis(windowRole);
    const profileQuery = useAppQuery(
        {
            ...trpc.ideaLab.getEntryTimingProfile.queryOptions({
                entityType,
                horizonSeconds: horizon ?? 1,
                lens,
                runId,
                variantId,
                windowRole
            }),
            enabled: horizon !== null,
            staleTime: Infinity
        },
        { fallbackErrorMessage: 'Failed to load Idea Lab entry timing' }
    );
    const createHypothesisMutation = useMutation({
        ...trpc.ideaLab.createEntryTimingHypothesis.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (variant) => {
            showToast(`Created timing hypothesis "${variant.label}"; opening its rules`, 'success');
            await queryClient.invalidateQueries(trpc.ideaLab.getStudy.pathFilter());
            void navigate(buildIdeaLabRulesEditorPath(variant.id));
        }
    });

    useEffect(() => {
        setSelectedCell(null);
    }, [entityType, horizon, lens, runId, variantId, windowRole]);

    if (horizon === null) return null;

    const profile = profileQuery.data;
    const selectedBucket =
        selectedCell?.intradayBucketKey === null || !profile
            ? null
            : (profile.intradayBuckets.find((bucket) => bucket.key === selectedCell?.intradayBucketKey) ?? null);
    const selectedBucketLabel =
        selectedBucket && profile && profile.semantics.intradayBinning.mode !== 'none'
            ? formatEntryTimingBucketLabel(selectedBucket, profile.semantics.intradayBinning.mode)
            : null;
    const selectedCellLabel = selectedCell ? `${selectedCell.weekday}${selectedBucketLabel ? ` · ${selectedBucketLabel}` : ''}` : null;
    const selectedEvidence = profile && selectedCell ? getEntryTimingCellEvidence(profile, selectedCell) : null;
    const createHypothesis = () => {
        if (!selectedCell) return;

        createHypothesisMutation.mutate(
            buildEntryTimingHypothesisInput({
                entityType,
                cell: selectedCell,
                horizonSeconds: horizon,
                lens,
                runId,
                variantId
            })
        );
    };

    return (
        <section aria-labelledby="idea-lab-entry-timing-heading" className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-zinc-400" id="idea-lab-entry-timing-heading">
                        Entry timing evidence
                    </h5>
                    <p className="mt-1 max-w-3xl text-xs text-zinc-500">
                        Decision-time patterns for this signal population. Use training to form a timing hypothesis, then check whether it
                        persists out of sample in walk-forward evidence.
                    </p>
                </div>
                <FormSelect
                    aria-label="Entry timing outcome horizon"
                    fullWidth={false}
                    label="Outcome horizon"
                    noMargin
                    options={horizons.map((candidate) => ({ label: formatHorizonLabel(candidate), value: candidate }))}
                    size="sm"
                    value={horizon}
                    onChange={(event) => setSelectedHorizon(Number(event.target.value))}
                />
            </div>

            {profileQuery.isLoading ? (
                <DataLoadingSpinner text="Loading entry timing..." />
            ) : profileQuery.error instanceof Error ? (
                <Callout variant="error">{profileQuery.error.message}</Callout>
            ) : profile ? (
                <>
                    <EntryTimingProfilePanel
                        profile={profile}
                        selectedCell={selectedCell}
                        title={`${variantLabel} · ${LENS_LABELS[lens]} entry timing`}
                        onCellSelect={hypothesisCreationEnabled ? setSelectedCell : undefined}
                    />
                    {hypothesisCreationEnabled ? (
                        selectedCell && selectedCellLabel ? (
                            <div className="rounded-lg border border-sky-900/70 bg-sky-950/20 p-4">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        <h6 className="text-sm font-semibold capitalize text-zinc-100">{selectedCellLabel}</h6>
                                        <p className="mt-1 text-xs text-zinc-400">
                                            Create an editable child of {variantLabel} with explicit weekday
                                            {selectedBucket ? ' and New York wall-clock' : ''} gates. The draft will retain this run, lens,
                                            horizon, and training cell as provenance.
                                        </p>
                                        {selectedEvidence?.thin && (
                                            <p className="mt-2 text-xs text-amber-300">
                                                Low-support cell: {selectedEvidence.reasons.join('; ')}. The draft can still be created, but
                                                requires stronger fresh evidence before promotion.
                                            </p>
                                        )}
                                    </div>
                                    <Button
                                        className="shrink-0"
                                        disabled={createHypothesisMutation.isPending}
                                        isLoading={createHypothesisMutation.isPending}
                                        variant="primary"
                                        onClick={createHypothesis}
                                    >
                                        Create timing hypothesis
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <Callout variant="info">Select a training heatmap cell to create a timing-hypothesis child variant.</Callout>
                        )
                    ) : (
                        <Callout variant="info">
                            Timing hypotheses can be created only from training evidence. Use walk-forward results to challenge a rule, not
                            to author it.
                        </Callout>
                    )}
                </>
            ) : null}
        </section>
    );
};

export default IdeaLabEntryTimingProfile;
